import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Button, Icon, Card } from "@weezevent/nacre";
import FormsWithEntryUsage, {
	CHECKBOX_FIELD_TYPE,
	SELECT_FIELD_TYPE,
	CONDITION_ISCHECKED,
	CONDITION_ISEMPTY,
	DEFAULT_CONDITIONS_BY_FIELD_TYPE,
	ATTENDEE_ENTRY_TYPE
} from "../../../../models/forms-with-entry-usage";
import style from "./FormFieldFilter.module.scss";
import FormSelector from "./FormSelector";
import FieldSelector from "./FieldSelector";
import ConditionSelector from "./ConditionSelector";
import ValueInput from "./ValueInput";

const FormFieldFilter = ({ value, onChange = () => {}, formsData }) => {
	const { t } = useTranslation();

	const formsModel = React.useMemo(() => {
		return formsData ? FormsWithEntryUsage.buildFromJSON(formsData) : null;
	}, [formsData]);

	const { attendeeForms, buyerForms, allForms } = React.useMemo(() => {
		if (!formsModel) {
			return { attendeeForms: [], buyerForms: [], allForms: [] };
		}
		return {
			attendeeForms: formsModel.getAttendeeEntries(),
			buyerForms: formsModel.getBuyerEntries(),
			allForms: formsModel.getAllForms()
		};
	}, [formsModel]);

	const filterValue = React.useMemo(() => value?.form_field_filter || {}, [
		value
	]);
	const selectedFormId = filterValue?.formId;
	const selectedEntryType = filterValue?.entryType;
	const selectedFieldId = filterValue?.fieldId;

	const handleChange = React.useCallback(
		newValue => onChange({ form_field_filter: newValue }),
		[onChange]
	);

	React.useEffect(() => {
		if (allForms.length === 1 && !filterValue.form_id) {
			handleChange({
				...filterValue,
				form_id: String(allForms[0].getFormPk())
			});
		}
	}, [allForms, filterValue, handleChange]);

	const handleFormChange = React.useCallback(
		(formId, entryType) => {
			handleChange({
				...filterValue,
				formId: formId || null,
				fieldId: null,
				entryType: entryType || null,
				condition: null,
				value: null
			});
		},
		[filterValue, handleChange]
	);

	const { attendeeFields, buyerFields } = React.useMemo(() => {
		if (!selectedFormId) return { attendeeFields: [], buyerFields: [] };

		const findFields = forms =>
			forms
				.find(f => String(f.getFormPk()) === String(selectedFormId))
				?.getFormEntries() || [];

		return {
			attendeeFields: findFields(attendeeForms),
			buyerFields: findFields(buyerForms)
		};
	}, [selectedFormId, attendeeForms, buyerForms]);

	const currentFields = React.useMemo(() => {
		if (!selectedFormId || !selectedEntryType) return [];

		const formsList =
			selectedEntryType === ATTENDEE_ENTRY_TYPE
				? attendeeForms
				: buyerForms;

		return (
			formsList
				.find(f => String(f.getFormPk()) === String(selectedFormId))
				?.getFormEntries() || []
		);
	}, [selectedFormId, selectedEntryType, attendeeForms, buyerForms]);

	const handleFieldChange = React.useCallback(
		fieldId => {
			const selectedField = currentFields.find(
				field => field.getFormFieldWeezId() === fieldId
			);
			const type = selectedField?.getFormFieldType();

			let condition = null;
			let value = null;

			if (type === CHECKBOX_FIELD_TYPE) {
				condition =
					DEFAULT_CONDITIONS_BY_FIELD_TYPE[CHECKBOX_FIELD_TYPE];
				value = false;
			} else if (type === SELECT_FIELD_TYPE) {
				condition = DEFAULT_CONDITIONS_BY_FIELD_TYPE[SELECT_FIELD_TYPE];
				value = [];
			}

			handleChange({
				...filterValue,
				fieldId: fieldId || null,
				entryType: selectedEntryType,
				condition,
				value
			});
		},
		[filterValue, currentFields, selectedEntryType, handleChange]
	);
	const selectedField = React.useMemo(() => {
		if (!selectedFieldId) return null;
		return (
			[...attendeeFields, ...buyerFields].find(
				f => f.getFormFieldWeezId() === selectedFieldId
			) || null
		);
	}, [selectedFieldId, attendeeFields, buyerFields]);

	const fieldType = selectedField?.getFormFieldType() || null;
	const selectedCondition = filterValue?.condition;

	const handleConditionChange = React.useCallback(
		condition => {
			handleChange({
				...filterValue,
				condition: condition || null,
				value:
					condition === CONDITION_ISEMPTY
						? null
						: condition === CONDITION_ISCHECKED
						? filterValue?.value || false
						: filterValue?.value
			});
		},
		[filterValue, handleChange]
	);

	const currentValue = filterValue?.value;
	const fieldChoices = selectedField?.getFormFieldChoices() || [];

	const handleValueChange = React.useCallback(
		newValue => {
			handleChange({
				...filterValue,
				value: newValue
			});
		},
		[filterValue, handleChange]
	);

	const handleDateChange = React.useCallback(
		(date, dateType) => {
			const valueObj = currentValue || {};
			const updatedValue = {
				...valueObj,
				[dateType]: date ? date.toISOString() : null
			};
			handleChange({
				...filterValue,
				value: updatedValue
			});
		},
		[filterValue, currentValue, handleChange]
	);

	return (
		<div className={style.formFieldFilterContainer}>
			<FormSelector
				attendeeForms={attendeeForms}
				buyerForms={buyerForms}
				selectedFormId={selectedFormId}
				selectedEntryType={selectedEntryType}
				onChange={handleFormChange}
				t={t}
			/>

			{selectedFormId && (
				<FieldSelector
					fields={currentFields}
					selectedFieldId={selectedFieldId}
					selectedEntryType={selectedEntryType}
					onChange={handleFieldChange}
					t={t}
				/>
			)}

			{selectedFieldId &&
				fieldType !== CHECKBOX_FIELD_TYPE &&
				fieldType !== SELECT_FIELD_TYPE && (
					<ConditionSelector
						fieldType={fieldType}
						selectedCondition={selectedCondition}
						onChange={handleConditionChange}
						t={t}
					/>
				)}

			<ValueInput
				fieldType={fieldType}
				selectedCondition={selectedCondition}
				fieldChoices={fieldChoices}
				currentValue={currentValue}
				onValueChange={handleValueChange}
				onDateChange={handleDateChange}
				t={t}
			/>
		</div>
	);
};

FormFieldFilter.propTypes = {
	value: PropTypes.object,
	onChange: PropTypes.func,
	formsData: PropTypes.object
};

FormFieldFilter.defaultProps = {
	value: {},
	onChange: () => {},
	formsData: {}
};

const FormFieldFilters = ({ value, onChange = () => {}, formsData }) => {
	const { t } = useTranslation();
	const [expandedFilters, setExpandedFilters] = React.useState(new Set([0]));

	const filtersArray = React.useMemo(() => {
		const formFieldFilter = Array.isArray(value)
			? value
			: value?.form_field_filter;

		if (!formFieldFilter) {
			return [{}];
		}

		if (Array.isArray(formFieldFilter)) {
			return formFieldFilter.length > 0 ? formFieldFilter : [{}];
		}

		if (
			typeof formFieldFilter === "object" &&
			Object.keys(formFieldFilter).length > 0
		) {
			return [formFieldFilter];
		}

		return [{}];
	}, [value]);

	React.useEffect(() => {
		if (filtersArray.length > expandedFilters.size) {
			setExpandedFilters(prev => {
				const newSet = new Set(prev);
				newSet.add(filtersArray.length - 1);
				return newSet;
			});
		}
	}, [filtersArray.length, expandedFilters.size]);

	const handleFilterChange = React.useCallback(
		(index, newFilterValue) => {
			const newFilters = [...filtersArray];
			newFilters[index] = newFilterValue;
			onChange({
				form_field_filter: newFilters
			});
		},
		[filtersArray, onChange]
	);

	const handleAddFilter = React.useCallback(() => {
		const newFilters = [...filtersArray, {}];
		onChange({
			form_field_filter: newFilters
		});
	}, [filtersArray, onChange]);

	const handleRemoveFilter = React.useCallback(
		index => {
			const newFilters = filtersArray.filter((_, i) => i !== index);
			setExpandedFilters(prev => {
				const newSet = new Set();
				prev.forEach(i => {
					if (i < index) {
						newSet.add(i);
					} else if (i > index) {
						newSet.add(i - 1);
					}
				});
				return newSet;
			});
			onChange({
				form_field_filter: newFilters.length > 0 ? newFilters : [{}]
			});
		},
		[filtersArray, onChange]
	);

	const isExpanded = React.useCallback(index => expandedFilters.has(index), [
		expandedFilters
	]);

	return (
		<div className={style.formFieldFiltersContainer}>
			{filtersArray.map((filterValue, index) => {
				const expanded = isExpanded(index);
				return (
					<Card key={index} className={style.filterCard}>
						<Card.Header className={style.filterCardHeader}>
							<Card.Title
								title={`${t(
									"access.attendees.attendeesList.filters.formFields.filterTitle"
								)} ${index + 1}`}
							/>
							<Button
								icon={<Icon name="cross" />}
								onClick={e => {
									e.stopPropagation();
									handleRemoveFilter(index);
								}}
								tertiary
								className={style.removeFilterButton}
							/>
						</Card.Header>
						{expanded && (
							<Card.Content>
								<FormFieldFilter
									value={{ form_field_filter: filterValue }}
									onChange={newValue =>
										handleFilterChange(
											index,
											newValue.form_field_filter
										)
									}
									formsData={formsData}
								/>
							</Card.Content>
						)}
					</Card>
				);
			})}
			<Button
				label={t(
					"access.attendees.attendeesList.filters.formFields.addFilter",
					"Add filter based on form field"
				)}
				icon={<Icon name="plus" />}
				onClick={handleAddFilter}
				primary
				className={style.addFilterButton}
			/>
		</div>
	);
};

FormFieldFilters.propTypes = {
	value: PropTypes.oneOfType([
		PropTypes.shape({
			form_field_filter: PropTypes.oneOfType([
				PropTypes.object,
				PropTypes.arrayOf(PropTypes.object)
			])
		}),
		PropTypes.object
	]),
	onChange: PropTypes.func,
	formsData: PropTypes.shape({
		attendee_entries: PropTypes.arrayOf(
			PropTypes.shape({
				form_pk: PropTypes.number,
				form_entries: PropTypes.array
			})
		),
		buyer_entries: PropTypes.arrayOf(
			PropTypes.shape({
				form_pk: PropTypes.number,
				form_entries: PropTypes.array
			})
		)
	})
};

FormFieldFilters.defaultProps = {
	value: {},
	onChange: () => {},
	formsData: {}
};

export { FormFieldFilter };
export default FormFieldFilters;
